import styled from "styled-components/macro";
import { Snackbar, TextField } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";

export const OverlayWrapper = styled.div<{
  $desktopRightOffset?: string;
}>`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.3);
  padding: 64px;
  overscroll-behavior: contain;

  @media (max-width: 600px) {
    align-items: center;
    overflow-y: auto;
    padding: 10px;
  }
`;

export const NonBlockingWrapper = styled.div<{
  $desktopRightOffset?: string;
}>`
  position: fixed;
  right: ${({ $desktopRightOffset }) => $desktopRightOffset ?? "24px"};
  bottom: 24px;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  width: calc(
    100% - ${({ $desktopRightOffset }) => $desktopRightOffset ?? "24px"} - 24px
  );
  pointer-events: none;
  z-index: 1400;
  transition:
    right 320ms cubic-bezier(0.2, 0, 0, 1),
    width 320ms cubic-bezier(0.2, 0, 0, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  @media (max-width: 900px) {
    right: 16px;
    bottom: 16px;
    width: calc(100% - 32px);
  }
`;

export const FeedbackModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 620px;
  box-shadow: 0px 2px 16px rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  overflow: hidden;

  @media (max-width: 600px) {
    max-width: 360px;
  }
`;

export const FeedbackModalWrapperNonBlocking = styled(FeedbackModalWrapper)`
  width: 620px;
  pointer-events: auto;
`;

export const Header = styled.div`
  background-color: ${({ theme }) => theme.neutral100};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px;
  height: 72px;
  box-sizing: border-box;
  border-bottom: 1px solid ${({ theme }) => theme.neutral100};

  @media (max-width: 600px) {
    padding: 24px 40px;
    height: auto;
  }
`;

export const HeaderTitle = styled.div`
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 1.2px;
  color: ${({ theme }) => theme.greyDark};
  text-transform: uppercase;
  text-align: left;
`;

export const CloseButton = styled(FontAwesomeIcon).attrs({
  icon: faXmark,
})<{ $disabled: boolean }>`
  align-self: flex-start;
  color: ${({ theme }) => theme.redMedium};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
`;

export const Body = styled.div`
  background-color: ${({ theme }) => theme.white};
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px 24px 48px;

  @media (max-width: 600px) {
    gap: 16px;
    padding: 24px;
  }
`;

export const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: ${({ theme }) => theme.greyDark};
`;

export const IntroTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
`;

export const IntroSubtitle = styled.div`
  font-size: 14px;
  line-height: 20px;
`;

export const ScoreRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding-top: 24px;

  @media (max-width: 600px) {
    gap: 8px;
    padding-top: 12px;
  }
`;

export const ScoreButton = styled.button<{ $selected: boolean }>`
  flex: 1 0 0;
  min-width: 0;
  background-color: ${({ $selected, theme }) =>
    $selected ? theme.redMedium : theme.neutral50};
  border: ${({ $selected, theme }) =>
    $selected ? `1px solid ${theme.redMedium}` : "1px solid transparent"};
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: ${({ $selected, theme }) =>
    $selected ? theme.white : theme.greyDark};

  @media (max-width: 600px) {
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    padding: 8px;
  }
`;

export const ScoreLabels = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 24px;
  font-size: 12px;
  line-height: 18px;
  color: ${({ theme }) => theme.greyMedium};

  @media (max-width: 600px) {
    padding: 0;
  }
`;

export const CommentField = styled(TextField)`
  width: 100%;
`;

export const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.redMedium};
  font-size: 14px;
`;

export const Footer = styled.div`
  background-color: ${({ theme }) => theme.white};
  border-top: 1px solid ${({ theme }) => theme.neutral100};
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 16px 24px;
  height: 72px;
  box-sizing: border-box;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;

  @media (max-width: 600px) {
    gap: 32px;
    padding: 32px 24px;
    height: auto;
  }
`;

export const FooterButton = styled.button<{
  $variant: "primary" | "secondary";
}>`
  background-color: ${({ $variant, theme }) =>
    $variant === "primary" ? theme.redMedium : theme.neutral100};
  color: ${({ $variant }) => ($variant === "primary" ? "#ffffff" : "#433f3f")};
  border: none;
  border-radius: 30px;
  padding: 11px 16px;
  min-height: 40px;
  min-width: 120px;
  cursor: pointer;
  font-weight: 700;
  font-size: 18px;
  line-height: 20px;
  letter-spacing: 0.18px;

  @media (max-width: 600px) {
    flex: 1;
    min-width: 0;
  }

  &:disabled {
    background-color: ${({ theme }) => theme.neutral100};
    color: ${({ theme }) => theme.greyMedium};
    cursor: not-allowed;
  }
`;

export const SubmittedWrapper = styled.div`
  background-color: ${({ theme }) => theme.white};
  display: flex;
  flex-direction: column;
  gap: 48px;
  align-items: flex-end;
  justify-content: center;
  padding: 32px;
  border-radius: 20px;
  width: 100%;
  max-width: 620px;
  pointer-events: auto;

  @media (max-width: 600px) {
    max-width: 360px;
    align-items: center;
  }
`;

export const ToastSnackbar = styled(Snackbar)`
  .MuiSnackbarContent-root {
    background-color: #323232;
    color: ${({ theme }) => theme.white};
    border-radius: 8px;
    box-shadow: 0px 2px 16px rgba(0, 0, 0, 0.05);
    width: 340px;
    min-height: 52px;
    box-sizing: border-box;
    padding: 6px 16px;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
  }

  .MuiSnackbarContent-message {
    padding: 0;
    flex: 1;
    font-size: 14px;
    line-height: 20px;
    white-space: normal;
    display: flex;
    align-items: center;
  }

  .MuiSnackbarContent-action {
    margin-left: auto;
    padding-left: 8px;
    align-self: center;
  }
`;

export const CancelToastContent = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CancelToastIcon = styled(FontAwesomeIcon)`
  flex-shrink: 0;
`;

export const SubmittedBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

export const SubmittedTitle = styled.div`
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  text-align: center;
  color: #272727;
`;

export const SubmittedSubtitle = styled.div`
  font-size: 20px;
  line-height: 28px;
  text-align: center;
  color: #272727;
`;

export const SubmittedButton = styled.button`
  background-color: #02665d;
  border: none;
  border-radius: 30px;
  height: 48px;
  width: 320px;
  max-width: 100%;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.white};
  cursor: pointer;
  align-self: center;

  @media (max-width: 600px) {
    display: none;
  }
`;
